import { api } from "@/lib/api";
import { useCallback, useEffect, useState } from "react";

export interface EnderecoPaciente {
  id?: number;
  cep?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  complemento?: string;
  principal?: boolean;
}

export interface TelefonePaciente {
  id?: number;
  telefone?: string;
  tipo?: string;
  principal?: boolean;
}

export interface PessoaPaciente {
  id: number;
  nome: string;
  data_nascimento?: string;
  pessoaFisica?: { cpf?: string };
  telefones?: TelefonePaciente[];
  enderecos?: EnderecoPaciente[];
  emails?: Array<{ email?: string }>;
}

export interface DocPacienteApi {
  id: number;
  tipo_documento: string;
  caminho_arquivo?: string;
  nome_arquivo?: string;
}

export interface PacienteLogado {
  id: number;
  id_pessoa: number;
  cartao_sus?: string;
  pessoa?: PessoaPaciente;
  documentos?: DocPacienteApi[];
  convenios?: unknown[];
}

export function usePacienteLogado() {
  const [paciente, setPaciente] = useState<PacienteLogado | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = (await api.get("/api/paciente/me")) as { result?: PacienteLogado };
      setPaciente(res?.result ?? null);
    } catch (e: any) {
      setError(e?.message ?? "Erro ao carregar dados do paciente.");
      setPaciente(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const nome = paciente?.pessoa?.nome ?? "";
  const email = paciente?.pessoa?.emails?.[0]?.email ?? "";
  const cpf = paciente?.pessoa?.pessoaFisica?.cpf ?? "";
  const cartaoSus = paciente?.cartao_sus ?? "";
  // Backend retorna pessoa.telefones[] com .telefone (string). Prioriza o principal.
  const listaTelefones = paciente?.pessoa?.telefones ?? [];
  const telPrincipal = listaTelefones.find((t: TelefonePaciente) => t.principal) ?? listaTelefones[0];
  const telefoneRaw = telPrincipal?.telefone ?? (telPrincipal as any)?.numero ?? "";
  const telefone = typeof telefoneRaw === "string" ? telefoneRaw : "";
  const enderecos = paciente?.pessoa?.enderecos ?? [];
  const enderecoPrincipal = enderecos.find((e) => e.principal) ?? enderecos[0];
  const endereco = (() => {
    const e = enderecoPrincipal;
    if (!e) return "";
    const parts = [e.logradouro, e.numero, e.complemento, e.bairro, e.cidade, e.uf, e.cep].filter(Boolean);
    return parts.join(", ");
  })();
  const dataNascimento = paciente?.pessoa?.data_nascimento
    ? new Date(paciente.pessoa.data_nascimento).toLocaleDateString("pt-BR")
    : "";

  return {
    paciente,
    loading,
    error,
    refetch,
    idPaciente: paciente?.id,
    nome,
    email,
    cpf,
    cartaoSus,
    telefone,
    endereco,
    enderecos,
    enderecoPrincipal,
    dataNascimento,
  };
}
